// packages/api-client/src/services/fileGroups.ts
import { getApiClient } from '../../client';
import { handleApiError } from '../../utils/handleApiError';
import type {
  FileGroup,
  FileGroupItem,
  FileGroupCreateRequest,
  FileGroupRenameRequest,
  FileGroupAddItemsRequest,
  FileGroupResolveResponse,
} from '@dopomogai/types';

type SR<T> = Promise<T | import('@dopomogai/types').ApiErrorResponse>;

const listGroups = (): SR<FileGroup[]> =>
  getApiClient()
    .get('/file-groups')
    .then(res => res.data)
    .catch(err => handleApiError(err, 'listing file groups'));

const createGroup = (payload: FileGroupCreateRequest): SR<FileGroup> =>
  getApiClient()
    .post('/file-groups', payload)
    .then(res => res.data)
    .catch(err => handleApiError(err, 'creating file group'));

const renameGroup = (groupId: string, payload: FileGroupRenameRequest): SR<FileGroup> =>
  getApiClient()
    .patch(`/file-groups/${groupId}`, payload)
    .then(res => res.data)
    .catch(err => handleApiError(err, 'renaming file group'));

const deleteGroup = (groupId: string): SR<{ ok: boolean }> =>
  getApiClient()
    .delete(`/file-groups/${groupId}`)
    .then(() => ({ ok: true }))
    .catch(err => handleApiError(err, 'deleting file group'));

const getItems = (groupId: string): SR<FileGroupItem[]> =>
  getApiClient()
    .get(`/file-groups/${groupId}/items`)
    .then(res => res.data)
    .catch(err => handleApiError(err, 'fetching group items'));

const addItems = (groupId: string, payload: FileGroupAddItemsRequest): SR<{ count: number }> =>
  getApiClient()
    .post(`/file-groups/${groupId}/items`, payload)
    .then(res => res.data)
    .catch(err => handleApiError(err, 'adding group items'));

const removeItem = (groupId: string, itemId: string): SR<{ ok: boolean }> =>
  getApiClient()
    .delete(`/file-groups/${groupId}/items/${itemId}`)
    .then(() => ({ ok: true }))
    .catch(err => handleApiError(err, 'removing group item'));

const resolve = (groupId: string): SR<FileGroupResolveResponse> =>
  getApiClient()
    .post(`/file-groups/${groupId}/resolve`, {})
    .then(res => res.data)
    .catch(err => handleApiError(err, 'resolving group'));

export const fileGroupsService = {
  listGroups,
  createGroup,
  renameGroup,
  deleteGroup,
  getItems,
  addItems,
  removeItem,
  resolve,
};